import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Button, Collapse, Paper } from '@mui/material';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

interface FilterPanelProps {
  children: ReactNode;
  onSearch: () => void;
  onReset?: () => void;
  defaultOpen?: boolean;
}

/**
 * Collapsible "Search Filters" block above a list. Fields are laid out in a responsive grid;
 * the list reloads on Search (or Enter), not on every keystroke.
 */
export function FilterPanel({ children, onSearch, onReset, defaultOpen = true }: FilterPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSearch();
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Button
        startIcon={<FilterListIcon />}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{ mb: 0.5 }}
      >
        Toggle filters
      </Button>
      <Collapse in={open}>
        <Paper
          variant="outlined"
          component="form"
          onSubmit={submit}
          sx={{ p: 1.5 }}
          aria-label="Search filters"
        >
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
              alignItems: 'start',
            }}
          >
            {children}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minHeight: 37 }}>
              <Button type="submit" variant="contained">
                Search
              </Button>
              {onReset ? (
                <Button type="button" variant="outlined" onClick={onReset}>
                  Reset
                </Button>
              ) : null}
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}
